import { parseMacOSReleaseMetadata, verifyMacOSReleaseFile } from './macosUpdate'

const isFinderMetadata = (name: string) => name === '.DS_Store' || name.startsWith('._')

export async function readMacOSReleaseFiles(input: File[]) {
  const files = input.filter(file => !isFinderMetadata(file.name))
  if (files.some(file => file.webkitRelativePath && file.webkitRelativePath.split('/').length !== 2)
    || new Set(files.map(file => file.webkitRelativePath?.split('/')[0] ?? '')).size > 1) {
    throw new Error('请选择直接包含 release.json 和 DMG 的发布文件夹')
  }
  const manifest = files.find(file => file.name === 'release.json')
  const packages = files.filter(file => file.name.toLowerCase().endsWith('.dmg'))
  if (files.length !== 2 || !manifest || packages.length !== 1) {
    throw new Error('请一起选择一个 release.json 和一个 DMG，或包含这两个文件的发布文件夹')
  }
  if (manifest.size > 64 * 1024) throw new Error('release.json 超出大小限制，请使用构建脚本生成的文件')
  const metadata = parseMacOSReleaseMetadata(JSON.parse(await manifest.text()))
  const file = packages[0]!
  if (metadata.size !== file.size) throw new Error('DMG 与发布元数据不匹配，请选择签名时使用的原始安装包')
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  const hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
  verifyMacOSReleaseFile(metadata, file, hash)
  return { metadata, file, hash }
}

export async function readMacOSReleaseDrop(transfer: DataTransfer): Promise<File[]> {
  // Capture entries during the drop event; browsers clear DataTransfer after it.
  const items = Array.from(transfer.items).filter(item => item.kind === 'file')
  const entries = items.map(item => item.webkitGetAsEntry?.())
  const folders = entries.filter(entry => entry?.isDirectory)
  if (!folders.length) return Array.from(transfer.files)
  if (items.length !== 1 || folders.length !== 1) throw new Error('请每次只拖入一个发布文件夹')
  const reader = (folders[0] as FileSystemDirectoryEntry).createReader()
  const files: File[] = []
  // Directory readers may return several batches, including on Chromium.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject))
    if (!batch.length) break
    for (const entry of batch) {
      if (isFinderMetadata(entry.name)) continue
      if (!entry.isFile || (entry.name !== 'release.json' && !entry.name.toLowerCase().endsWith('.dmg'))) {
        throw new Error('请选择直接包含 release.json 和 DMG 的发布文件夹')
      }
      files.push(await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject)))
      if (files.length > 2) throw new Error('发布文件夹中只能包含一个 release.json 和一个 DMG')
    }
  }
  return files
}
